import { Component, Inject, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { MessageService } from 'primeng/api';

import { AbstractControl, AsyncValidatorFn, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Empresas } from '@p-p-portafolio/interfaces/empresa';

import { Estatus } from '@p-p-portafolio/interfaces/estatus';
import { SwalService } from '@shared/utils/swal.service';
import { UserService } from '@pages/user/services/user.service';
import { EstatusService } from '@p-p-portafolio/services/estatus.service';
import { SubEstatusService } from '@p-p-portafolio/services/sub-estatus.service';
import { InjectableService } from '@shared/utils/injectable.service';
import { CampoTablaWhereService } from '@shared/services/campo-tabla-where.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app_estatus-form',
  templateUrl: './estatus-form.component.html',
  styleUrls: ['./estatus-form.component.css'],
})
export class EstatusFormComponent implements OnInit {
  private _user: string = this._userServices.userCurrent;
  public Empresas: Empresas[];
  public loading: boolean = false;
  public selectEmpresa: boolean = true;
  public displaySubestatusDialog: boolean = false;
  public subestatusColumns = [
    { field: 'subEstatus', header: 'Sub Estatus Crédito' },
    { field: 'descripcion', header: 'Descripción' },
    { field: 'actions', header: 'Acciones' }
  ];
  public subestatusDataSource: Array<{ subEstatus: string; descripcion: string }> = [];
  public subestatusLoading: boolean = false;
  public displaySubestatusFormDialog: boolean = false;
  public subestatusForm: UntypedFormGroup;
  public subestatusFormSubmitting: boolean = false;
  public subestatusDialogMode: 'create' | 'edit' = 'create';
  public subestatusEditingRow: any = null;

  constructor(
    private _fb: UntypedFormBuilder,
    private _dialogRef: MatDialogRef<EstatusFormComponent>,
    private _swalService: SwalService,
    private _estatusServices: EstatusService,
    private _ctwServices: CampoTablaWhereService,
    private _userServices: UserService,
    private _injectServices: InjectableService,
    private _subEstatusService: SubEstatusService,
    private _messageService: MessageService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public model: Estatus
  ) {
    if (this.model !== null) {
      const setModel = {
        sEmpresa: model.sEmpresa,
        codigoEstado: model.codigoEstado,
        descripcion: model.descripcion,
        user: this._user,
      };
      this.form.setValue(setModel);
      this.form.get('sEmpresa')?.disable();
      this.form.get('codigoEstado')?.disable();
    }

    this.subestatusForm = this._fb.group({
      compania: [{ value: '', disabled: true }, Validators.required],
      estatusCredito: [{ value: '', disabled: true }, Validators.required],
      subEstatusCredito: ['', Validators.required],
      descripcion: ['', Validators.required]
    });
  }

  form = this._fb.group({
    sEmpresa: ['', Validators.required],
    codigoEstado: ['',
      {
        validators: [Validators.required],
        asyncValidators: [this.singleCodigo()],
        updateOn: 'blur',
      },],
    descripcion: ['', Validators.required],
    user: this._user,
  });

  ngOnInit(): void {
    this.readEmpresas();
    this._injectServices.disparadorEmpresa.subscribe((res) => {
      this.form.patchValue({ sEmpresa: res.model.data });
      this.selectEmpresa = res.model.success;
    });
  }

  close() {
    this._dialogRef.close();
  }

  buscarEmpresas() {
    if (this.selectEmpresa) this.selectEmpresa = false;
    else this.selectEmpresa = true;
  }

  readEmpresas() {
    this._ctwServices.empresas().subscribe((res) => {
      this.Empresas = res.data;
    });
  }

  create(model: Estatus) {
    if (this.form.valid) {
      this.loading = true;
      this._estatusServices.create(model).subscribe((res) => {
        this.loading = false;
        if (res.exito === 1) {
          this._swalService.success('Agregado');
          this._dialogRef.close(true);
        } else this._swalService.error('No Agregado');
      });
    }
  }

  update(model: Estatus) {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      this._estatusServices.update(formValue).subscribe((res) => {
        this.loading = false;
        if (res.exito === 1) {
          this._swalService.success('Editado');
          this._dialogRef.close(true);
        } else this._swalService.error('No Editado');
      });
    }
  }

  openCustomizables(): void {
    const dialogRef = this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: "1000px",
      height: "1000px",
      disableClose: true,
      panelClass: ["custom-dialog-zindex"],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: "estadocredito",
        sClave: `${this.model?.codigoEstado}`,
        sNombre: this.model?.descripcion || '',
        sTipo: "Opcion"
      }
    });
  }

  openSubestatusDialog(): void {
    if (!this.model) {
      return;
    }
    this.displaySubestatusDialog = true;
    this.loadSubestatusList();
  }

  hideSubestatusDialog(): void {
    this.displaySubestatusDialog = false;
  }

  openNewSubestatusDialog(): void {
    const formValues = this.form.getRawValue();
    this.subestatusDialogMode = 'create';
    this.subestatusEditingRow = null;
    this.subestatusForm.reset({
      compania: (formValues.sEmpresa || this.model?.sEmpresa || '').toString().trim(),
      estatusCredito: this.model?.codigoEstado || formValues.codigoEstado || '',
      subEstatusCredito: '',
      descripcion: ''
    });
    this.subestatusForm.enable();
    this.subestatusForm.get('compania')?.disable();
    this.subestatusForm.get('estatusCredito')?.disable();
    this.displaySubestatusFormDialog = true;
  }

  editSubestatus(row: any): void {
    const formValues = this.form.getRawValue();
    this.subestatusDialogMode = 'edit';
    this.subestatusEditingRow = row;
    this.subestatusForm.reset({
      compania: (formValues.sEmpresa || this.model?.sEmpresa || '').toString().trim(),
      estatusCredito: this.model?.codigoEstado || formValues.codigoEstado || '',
      subEstatusCredito: row?.subEstatus || '',
      descripcion: row?.descripcion || ''
    });
    this.subestatusForm.enable();
    this.subestatusForm.get('compania')?.disable();
    this.subestatusForm.get('estatusCredito')?.disable();
    this.displaySubestatusFormDialog = true;
  }

  hideSubestatusFormDialog(): void {
    this.displaySubestatusFormDialog = false;
    this.subestatusFormSubmitting = false;
    this.subestatusForm.enable();
    this.subestatusForm.get('compania')?.disable();
    this.subestatusForm.get('estatusCredito')?.disable();
    this.subestatusEditingRow = null;
    this.subestatusDialogMode = 'create';
  }

  saveSubestatus(): void {
    if (this.subestatusFormSubmitting) {
      return;
    }

    if (this.subestatusForm.invalid) {
      this.subestatusForm.markAllAsTouched();
      return;
    }

    this.subestatusFormSubmitting = true;

    const { compania, estatusCredito, subEstatusCredito, descripcion } = this.subestatusForm.getRawValue();
    const payload = {
      que: this.subestatusDialogMode === 'edit' ? 2 : 1,
      tope: '',
      distribuidor: compania,
      estatusCredito,
      subEstatusCredito,
      descirpcion: descripcion
    };

    const request$ = this.subestatusDialogMode === 'edit'
      ? this._subEstatusService.updateSubEstatus(payload)
      : this._subEstatusService.addSubEstatus(payload);

    request$.subscribe({
      next: (res) => {
        const response: any = res || {};
        const isSuccess = response?.success === true || response?.exito === 1 || response?.data === true;
        if (isSuccess) {
          this._messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail:
              this.subestatusDialogMode === 'edit'
                ? 'Subestatus actualizado correctamente.'
                : 'Subestatus creado correctamente.'
          });
          this.hideSubestatusFormDialog();
          this.loadSubestatusList();
        } else {
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No fue posible guardar el subestatus.'
          });
        }
        this.subestatusFormSubmitting = false;
        this.subestatusEditingRow = null;
        this.subestatusDialogMode = 'create';
      },
      error: () => {
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ocurrió un error al guardar el subestatus.'
        });
        this.subestatusFormSubmitting = false;
        this.subestatusEditingRow = null;
        this.subestatusDialogMode = 'create';
      }
    });
  }

  deleteSubestatus(row: any): void {
    if (!row) {
      return;
    }

    Swal.fire({
      title: 'Confirmar',
      text: '¿Deseas eliminar este subestatus?',
      icon: 'warning',
      iconColor: "#d33",
      confirmButtonColor: "#d33",
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      heightAuto: false
    }).then((result) => {
      if (result.isConfirmed) {
        const formValues = this.form.getRawValue();
        const payload = {
          que: 3,
          tope: '',
          distribuidor: formValues.sEmpresa || this.model?.sEmpresa || '',
          estatusCredito: formValues.codigoEstado || this.model?.codigoEstado || '',
          subEstatusCredito: row?.subEstatus || '',
          descirpcion: ''
        };

        this.subestatusLoading = true;
        this._subEstatusService.deleteSubEstatus(payload).subscribe({
          next: (res) => {
            const response: any = res || {};
            const isSuccess = response?.success === true || response?.exito === 1 || response?.data === true;
            if (isSuccess) {
              this._messageService.add({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Subestatus eliminado.'
              });
              this.loadSubestatusList();
            } else {
              this._messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No fue posible eliminar el subestatus.'
              });
              this.subestatusLoading = false;
            }
          },
          error: () => {
            this._messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Ocurrió un error al eliminar el subestatus.'
            });
            this.subestatusLoading = false;
          }
        });
      }
    });
  }

  private loadSubestatusList(): void {
    this.subestatusLoading = true;
    const formValues = this.form.getRawValue();
    const payload = {
      que: 4,
      tope: '9999',
      distribuidor: formValues.sEmpresa || this.model?.sEmpresa || '',
      estatusCredito: formValues.codigoEstado || this.model?.codigoEstado || '',
      subEstatusCredito: '',
      descirpcion: ''
    };

    this._subEstatusService.getSubEstatusList(payload).subscribe({
      next: (res) => {
        const responseData: any = res || {};
        const isSuccess = responseData?.success ?? responseData?.exito === 1;
        if (isSuccess && Array.isArray(responseData?.data)) {
          this.subestatusDataSource = responseData.data.map((item: any) => ({
            subEstatus: item.subEstatusCredito,
            descripcion: item.descripcion
          }));
        } else {
          this.subestatusDataSource = [];
        }
        this.subestatusLoading = false;
      },
      error: () => {
        this.subestatusLoading = false;
        this.subestatusDataSource = [];
        this._swalService.error('No se pudieron cargar los subestatus.');
      }
    });
  }

  //#region Get Fb
  formValue(value: string) {
    return this.form.get(value);
  }
  //#endregion

  //#region AsyncValidation
  singleCodigo(): AsyncValidatorFn {
    return (
      control: AbstractControl
    ): Promise<ValidationErrors> | Observable<any> => {
      if (this.model) return of(true);
      else {
        return this._estatusServices.readOne(control.value).pipe(
          map((res) => {
            if (res.exito === 1) return { singleCodigo: true };
            return null;
          })
        );
      }
    };
  }
  //#endregion
}
